import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Moon, Eye, EyeOff } from "lucide-react";
import NeuralCanvas from "@/components/NeuralCanvas";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { requestDeviceUnlock } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.next || "/campus";
  const { login } = useAuth();
  const { light, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const pedirDesbloqueo = async () => {
    try { await requestDeviceUnlock(email.trim()); toast.success("Solicitud enviada. El equipo la revisará y te avisará cuando puedas entrar desde este dispositivo."); setBlocked(false); }
    catch { toast.error("No se pudo enviar la solicitud. Inténtalo más tarde."); }
  };

  const submit = async () => {
    if (loading) return;
    if (email.trim().length < 3) { toast.error("Introduce tu email o nombre de usuario."); return; }
    if (password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres."); return; }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("¡Bienvenido/a al campus!");
      navigate(next);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 400) {
        toast.error("Email/usuario o contraseña incorrectos.");
      } else if (status === 403) {
        setBlocked(true);
        toast.error(formatApiError(e?.response?.data?.detail) || "Acceso bloqueado en este dispositivo.");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo iniciar sesión. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={light ? "lci light" : "lci"}><div className="login-wrap">
      <NeuralCanvas />
      <button className="theme-toggle" data-testid="theme-toggle" onClick={toggle}
        aria-label={light ? "Activar modo oscuro" : "Activar modo claro"} title={light ? "Modo oscuro" : "Modo claro"}
        style={{ position: "absolute", top: 20, right: 20, zIndex: 5 }}>
        {light ? <Moon size={17} /> : <Sun size={17} />}
      </button>
      <div className="login-card rise" data-testid="login-card">
        <div className="logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <div className="logo-mark">L</div>
          <div className="logo-txt">LIBERTI CLUB <em>IA</em>
          </div>
        </div>
        <h2>Campus Virtual</h2>
        <p className="hint">Accede a tus programas, mentorías y comunidad.</p>

        <div className="field">
          <label htmlFor="lci-email">Email o usuario</label>
          <input id="lci-email" data-testid="login-email" type="text" autoCapitalize="none" autoCorrect="off" spellCheck="false" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com o tu usuario" onKeyDown={(e) => e.key === "Enter" && submit()} />
        </div>
        <div className="field">
          <label htmlFor="lci-password">Contraseña</label>
          <div className="pass-wrap">
            <input id="lci-password" data-testid="login-password" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña" onKeyDown={(e) => e.key === "Enter" && submit()} />
            <button type="button" className="pass-eye" data-testid="toggle-password" onClick={() => setShowPass((v) => !v)}
              aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"} title={showPass ? "Ocultar" : "Mostrar"}>
              {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>
        <button className="btn btn-gold" data-testid="login-submit" style={{ width: "100%", justifyContent: "center" }} onClick={submit}>
          {loading ? "Procesando…" : "Entrar al campus →"}
        </button>
        <div className="device-note" data-testid="device-note">
          🔒 Por seguridad, cada cuenta solo puede usarse en <b>1 ordenador</b> y <b>1 móvil</b>. Si intentas entrar desde otro dispositivo, el acceso se bloquea.
          {blocked && (
            <button className="unlock-btn" data-testid="solicitar-desbloqueo" onClick={pedirDesbloqueo}>
              Solicitar cambio de dispositivo
            </button>
          )}
        </div>
        <p className="hint" style={{ marginTop: 18, textAlign: "center" }}>
          ¿Aún no tienes acceso? Adquiere un curso o el VIP y te daremos tus credenciales.
        </p>
        <button className="back" style={{ marginTop: 16 }} onClick={() => navigate("/")}>← Volver a la web</button>
      </div>
    </div></div>
  );
}
